import { Check, Flame } from 'lucide-react';
import { motion } from 'motion/react';
import { type FC, type ReactNode, useState } from 'react';
import classNames from 'classnames';

import { CloseButton, Heading, Button } from '../../shared/ui';

export interface HotOfferCardProps {
  title: string;
  description: string;
  /** Текст основной кнопки (если не передан — будет "Принять") */
  primaryLabel?: string;
  /** Текст вторичной кнопки (если не передан — будет "Позже") */
  secondaryLabel?: string;
  /** Обработчик основной кнопки (deprecated, оставлено для совместимости) */
  onPrimaryAction?: () => void;
  /** Обработчик основной кнопки */
  onAccept?: () => void;
  onDismiss?: () => void;
  /** Иконка слева от заголовка (по умолчанию — Flame из lucide) */
  icon?: ReactNode;
  className?: string;
}

export const HotOfferCard: FC<HotOfferCardProps> = ({
  title,
  description,
  onAccept,
  primaryLabel,
  secondaryLabel,
  onPrimaryAction,
  onDismiss,
  icon,
  className,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      onDismiss?.();
    }, 300);
  };

  const handleAccept = () => {
    onAccept?.();
    onPrimaryAction?.();
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={classNames(
        'relative rounded-control border-2 border-accent bg-gradient-to-r from-warning-surface to-danger-surface p-6 shadow-raised',
        className,
      )}
    >
      <motion.div
        className="absolute inset-0 rounded-control bg-gradient-to-r from-accent-soft to-danger-surface opacity-0"
        animate={{
          opacity: [0, 0.3, 0],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      <CloseButton className="absolute top-3 right-3 -m-1.5" onClick={handleDismiss} />

      <div className="relative space-y-4">
        <div className="flex items-start gap-3">
          <motion.div
            animate={{
              rotate: [0, 10, -10, 10, 0],
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              repeatDelay: 2,
            }}
            className="shrink-0 text-primary"
            aria-hidden
          >
            {icon ?? <Flame className="h-6 w-6" />}
          </motion.div>
          <div className="min-w-0 flex-1">
            <Heading size="sm">{title}</Heading>
            <p className="mt-2 leading-relaxed text-ink-muted">{description}</p>
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            type="button"
            size="lg"
            className="flex-1"
            startIcon={<Check className="h-5 w-5 shrink-0" />}
            onClick={handleAccept}
          >
            {primaryLabel ?? 'Принять'}
          </Button>
          <Button type="button" size="lg" variant="neutral" onClick={handleDismiss}>
            {secondaryLabel ?? 'Позже'}
          </Button>
        </div>
      </div>
    </motion.div>
  );
};
